import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { 
  Settings, 
  ChevronLeft, 
  ChevronRight, 
  Home, 
  ShoppingCart, 
  Users, 
  Receipt, 
  TrendingUp,
  Menu,
  Bell,
  Wallet,
  Trophy,
  Megaphone,
  Flame,
  Gift,
} from "lucide-react";

import profileImage from "assets/profile.jpeg";

// Nav items
const navItems = [
  {
    text: "Dashboard",
    icon: <Home className="w-5 h-5" />,
    path: "dashboard"
  },
  {
    text: "General",
    icon: null,
  },
  {
    text: "Category",
    icon: <ShoppingCart className="w-5 h-5" />,
    path: "category"
  },
  {
    text: "Products",
    icon: <ShoppingCart className="w-5 h-5" />,
    path: "products"
  },
  {
    text: "Customers",
    icon: <Users className="w-5 h-5" />,
    path: "customers"
  },
  {
    text: "Transactions",
    icon: <Receipt className="w-5 h-5" />,
    path: "transactions"
  },
  {
    text: "Withdraw requests",
    icon: <Wallet className="w-5 h-5" />,
    path: "withdraw-requests"
  },
  {
    text: "Questions",
    icon: <TrendingUp className="w-5 h-5" />,
    path: "questions"
  },
  {
    text: "Prize Leaderboard",
    icon: <Trophy className="w-5 h-5" />,
    path: "prize-leaderboard"
  },
  {
    text: "Listed Leaderboards",
    icon: <Trophy className="w-5 h-5" />,
    path: "listed-leaderboards"
  },
  {
    text: "Leaderboard streak",
    icon: <Flame className="w-5 h-5" />,
    path: "leaderboard-streak"
  },
  {
    text: "Referral message",
    icon: <Gift className="w-5 h-5" />,
    path: "referral-message"
  },
  {
    text: "Notifications",
    icon: <Bell className="w-5 h-5" />,
    path: "notifications"
  },
  {
    text: "Ads settings",
    icon: <Megaphone className="w-5 h-5" />,
    path: "ads-settings"
  },
  {
    text: "Settings",
    icon: <Settings className="w-5 h-5" />,
    path: "settings"
  },
];

// Sidebar
const Sidebar = ({
  user,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
}) => {
  // config
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();

  // set active path
  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  if (!isSidebarOpen) {
    return (
      <div className="fixed left-0 top-0 z-40 h-screen w-16 bg-card border-r">
        <div className="flex flex-col items-center py-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsSidebarOpen(true)}
            className="mb-4"
          >
            <Menu className="w-5 h-5" />
          </Button>
          {navItems.map(({ text, icon, path }) => {
            if (!icon || !path) return null;
            const isActive = active === path;
            return (
              <Button
                key={text}
                variant={isActive ? "secondary" : "ghost"}
                size="icon"
                onClick={() => {
                  navigate(`/${path}`);
                  setActive(path);
                }}
                className={`mb-2 ${isActive ? 'bg-secondary' : ''}`}
                title={text}
              >
                {icon}
              </Button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed left-0 top-0 z-40 h-screen w-64 bg-card border-r transition-transform duration-300 ease-in-out">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">W</span>
            </div>
            <span className="font-bold text-lg">WishAur</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsSidebarOpen(false)}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navItems.map(({ text, icon, path }) => {
            if (!icon) {
              return (
                <div key={text} className="px-3 py-2">
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                    {text}
                  </h3>
                </div>
              );
            }

            const isActive = active === path;
            return (
              <Button
                key={text}
                variant={isActive ? "secondary" : "ghost"}
                className={`w-full justify-start ${isActive ? 'bg-secondary' : ''}`}
                onClick={() => {
                  navigate(`/${path}`);
                  setActive(path);
                }}
              >
                <span className="mr-3">{icon}</span>
                {text}
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Button>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t">
          <div className="flex items-center space-x-3">
            <img
              src={profileImage}
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {user.name || "Admin User"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user.occupation || "Administrator"}
              </p>
            </div>
            <Button variant="ghost" size="icon">
              <Settings className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
